import React, { useMemo, memo } from 'react'
import { Card } from 'transfix-ui/components/Card'
import { Typography, Variant } from 'transfix-ui/core'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { getLoadingMethods } from 'apps/fleet-planner/utils/dedicatedLanesHelpers'
import { DedicatedLanesResponse } from 'apps/marketplace/apiTypes/dedicatedLanes'
import { Bullet } from 'components/atoms'
import { DISPLAY } from 'constants/display'
import { MARKETPLACE_EQUIPMENT_TYPES } from 'constants/equipment'
import { InlineRow } from '../load-details/InlineRow'

interface IDedicatedLanesLoadDetailsProps {
  dedicatedLane?: DedicatedLanesResponse
  showMiles?: boolean
  textSize?: 'large' | 'medium'
}

export const DedicatedLanesLoadDetails = memo<IDedicatedLanesLoadDetailsProps>(
  ({ dedicatedLane = {}, showMiles = false, textSize = 'medium' }) => {
    const { isMobile } = useBreakpointContext()
    const { getFlag } = useFeatureFlagContext()
    const { enabled: isListingDetailsEnabled } = getFlag('fmsDedicatedLaneListingDetails')
    const { equipment, listingDetails, shipperMiles } = dedicatedLane ?? {}
    const { commodity, maxWeight, minWeight, offerLeadTime } = listingDetails ?? {}

    const formattedMiles = shipperMiles ? `${shipperMiles} mi` : DISPLAY.empty

    const formattedWeight = useMemo(() => {
      if (minWeight && maxWeight) {
        return `${minWeight} lbs - ${maxWeight} lbs`
      }
      if (maxWeight) {
        return `${maxWeight} lbs`
      }

      return DISPLAY.empty
    }, [maxWeight, minWeight])

    const formattedOfferLeadTime = useMemo(() => {
      if (!offerLeadTime) {
        return DISPLAY.empty
      }

      return `Usually ${offerLeadTime} ${offerLeadTime > 1 ? 'Days' : 'Day'}`
    }, [offerLeadTime])

    // Default to dry_van
    const { parentId = 3 } = equipment || {}
    const equipmentName = MARKETPLACE_EQUIPMENT_TYPES.find(eq => eq.id === parentId)?.name
    const { deliveryMethod, pickupMethod } = getLoadingMethods(dedicatedLane) || {}
    const typographyVariant: Variant = textSize === 'large' ? 'body1' : 'body2'

    return (
      <Card>
        <Typography variant='subtitle1' className={isMobile ? 'mb3' : ''}>
          Load Details
        </Typography>
        <InlineRow
          bodyClass={isMobile ? 'mt1 mb2' : 'mt1'}
          rowTitle={<Typography variant={typographyVariant}>Equipment Type</Typography>}
          rowBody={
            <Typography
              data-test='dedicated-lanes-load-details-equipment'
              variant={typographyVariant}>
              {equipmentName}
            </Typography>
          }
        />
        <InlineRow
          bodyClass='mt1'
          rowTitle={<Typography variant={typographyVariant}>Loading Method</Typography>}
          rowBody={
            <Typography
              data-test='dedicated-lanes-load-details-loading-method'
              variant={typographyVariant}>
              {pickupMethod} <Bullet /> {deliveryMethod}
            </Typography>
          }
        />
        {showMiles && (
          <InlineRow
            bodyClass='mt1'
            rowTitle={<Typography variant={typographyVariant}>Loaded Miles</Typography>}
            rowBody={
              <Typography
                variant={typographyVariant}
                data-test='dedicated-lanes-load-details-shipper-miles'>
                {formattedMiles}
              </Typography>
            }
          />
        )}

        {isListingDetailsEnabled && (
          <>
            <InlineRow
              bodyClass='mt1'
              rowTitle={<Typography variant={typographyVariant}>Commodity</Typography>}
              rowBody={
                <Typography
                  data-test='dedicated-lanes-load-details-commodity'
                  variant={typographyVariant}>
                  {commodity ?? DISPLAY.empty}
                </Typography>
              }
            />

            <InlineRow
              bodyClass='mt1'
              rowTitle={<Typography variant={typographyVariant}>Weight</Typography>}
              rowBody={
                <Typography
                  data-test='dedicated-lanes-load-details-weight'
                  variant={typographyVariant}>
                  {formattedWeight}
                </Typography>
              }
            />

            <InlineRow
              bodyClass='mt1'
              rowTitle={<Typography variant={typographyVariant}>Offer Lead Time</Typography>}
              rowBody={
                <Typography
                  data-test='dedicated-lanes-load-details-offer-lead-time'
                  variant={typographyVariant}>
                  {formattedOfferLeadTime}
                </Typography>
              }
            />
          </>
        )}
      </Card>
    )
  }
)
